
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Badge, Spinner } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';

const initialFieldValues = {
    staffId: '00000000-0000-0000-0000-000000000000',
    salonId: '',
    name: '',
    specializations: '', // Matching your Swagger property name
    isActive: true
};

const Staff = () => {
    // API & Data State
    const [lists, setLists] = useState([]);
    const [salonLists, setSalonLists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);

    // Form & UI State
    const [showModal, setShowModal] = useState(false);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const THEME_COLOR = '#065f46';

    // --- API CALLS ---

    const GetData = async (pNum) => {
        setLoading(true);
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETALLSTAFF, {
                params: { pageSize: 10, pageNumber: pNum }
            });
            if (res.data) {
                const result = Array.isArray(res.data) ? res.data : (res.data.data || []);
                setLists(result);
            }
        } catch (error) {
            console.error("Error fetching staff", error);
        } finally {
            setLoading(false);
        }
    };

    const GetSalons = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETALLSALON);
            const result = Array.isArray(res.data) ? res.data : (res.data.data || []);
            setSalonLists(result);
        } catch (error) {
            console.error("Error fetching salons", error);
        }
    };

    useEffect(() => {
        GetData(pageNumber);
        GetSalons();
    }, [pageNumber]);

    // --- HANDLERS ---

    const handleInput = (e) => {
        const { name, value, type, checked } = e.target;
        setValues({ 
            ...values, 
            [name]: type === 'checkbox' ? checked : value 
        });
    };

    const validate = () => {
        let temp = {};
        temp.name = values.name ? "" : "Staff Name is required";
        temp.salonId = (values.salonId && values.salonId !== '00000000-0000-0000-0000-000000000000') ? "" : "Please select a Salon";
        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setBtnLoading(true);
            try {
                const isNew = values.staffId === "00000000-0000-0000-0000-000000000000";
                if (isNew) {
                    await axios.post(config.APIACTIVEURL + config.CREATESTAFF, values);
                } else {
                    await axios.put(config.APIACTIVEURL + config.UPDATESTAFF, values);
                }
                setShowModal(false);
                setValues(initialFieldValues);
                GetData(pageNumber);
            } catch (error) {
                alert("Error saving staff details.");
            } finally {
                setBtnLoading(false);
            }
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${config.APIACTIVEURL}${config.DELETESTAFF}/${itemToDelete}`);
            setShowDeleteModal(false);
            GetData(pageNumber);
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="page-content" style={{ paddingTop: "90px", minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <div className="container-fluid px-lg-5 py-4">
                
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold text-dark mb-1">Staff Management</h3>
                        <p className="text-muted small">Manage professionals and their specializations</p>
                    </div>
                    <Button 
                        style={{ backgroundColor: THEME_COLOR }} 
                        className="border-0 px-4 py-2 fw-bold shadow-sm"
                        onClick={() => { setValues(initialFieldValues); setShowModal(true); }}
                    >
                        <i className="ri-user-add-line me-2"></i> Add New Staff
                    </Button>
                </div>

                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4 py-3">Professional</th>
                                    <th>Salon Branch</th>
                                    <th>Specializations</th>
                                    <th>Status</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" className="text-center py-5"><Spinner animation="border" variant="success" /></td></tr>
                                ) : lists.map((item) => (
                                    <tr key={item.staffId}>
                                        <td className="ps-4">
                                            <div className="fw-bold text-dark">{item.name}</div>
                                        </td>
                                        <td>
                                            <div className="small text-secondary">
                                                {item.salon?.name || 'Unassigned'}
                                            </div>
                                        </td>
                                        <td>
                                            {item.specializations ? item.specializations.split(',').map((spec, i) => (
                                                <Badge key={i} bg="info-subtle" text="info" className="me-1 border border-info-subtle fw-normal">
                                                    {spec.trim()}
                                                </Badge>
                                            )) : <span className="text-muted small fst-italic">No specializations</span>}
                                        </td>
                                        <td>
                                            <Badge pill className={item.isActive ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}>
                                                {item.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            <button className="btn btn-sm btn-light rounded-circle me-1" onClick={() => { setValues(item); setShowModal(true); }}>
                                                <i className="ri-edit-2-line text-primary fs-5"></i>
                                            </button>
                                            <button className="btn btn-sm btn-light rounded-circle" onClick={() => { setItemToDelete(item.staffId); setShowDeleteModal(true); }}>
                                                <i className="ri-delete-bin-line text-danger fs-5"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* FORM MODAL */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fw-bold">Staff Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-bold text-muted">Full Name</Form.Label>
                                <Form.Control 
                                    name="name"
                                    value={values.name}
                                    onChange={handleInput}
                                    placeholder="Enter name"
                                    className={errors.name ? 'is-invalid' : ''}
                                />
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-bold text-muted">Assign Salon</Form.Label>
                                <Form.Select 
                                    name="salonId"
                                    value={values.salonId}
                                    onChange={handleInput}
                                    className={errors.salonId ? 'is-invalid' : ''}
                                >
                                    <option value="">Choose Salon...</option>
                                    {salonLists.map(s => (
                                        <option key={s.salonId} value={s.salonId}>{s.name}</option>
                                    ))}
                                </Form.Select>
                                {errors.salonId && <div className="invalid-feedback">{errors.salonId}</div>}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-bold text-muted">Specializations</Form.Label>
                                <Form.Control 
                                    name="specializations"
                                    value={values.specializations || ''}
                                    onChange={handleInput}
                                    placeholder="e.g. Haircut, Massage, Facial"
                                />
                                <Form.Text className="text-muted">Separate multiple skills with commas.</Form.Text>
                            </Form.Group>

                            <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded-3 mb-4">
                                <span className="fw-bold small">Active Status</span>
                                <Form.Check 
                                    type="switch"
                                    name="isActive"
                                    checked={values.isActive}
                                    onChange={handleInput}
                                />
                            </div>

                            <Button 
                                type="submit" 
                                className="w-100 border-0 py-2 fw-bold" 
                                style={{ backgroundColor: THEME_COLOR }}
                                disabled={btnLoading}
                            >
                                {btnLoading ? <Spinner size="sm" /> : 'Save Professional'}
                            </Button>
                        </Form>
                    </Modal.Body>
                </Modal>

                {/* DELETE MODAL */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
                    <Modal.Body className="text-center py-4">
                        <div className="text-danger mb-3"><i className="ri-error-warning-line fs-1"></i></div>
                        <h5 className="fw-bold">Remove Staff?</h5>
                        <p className="text-muted small">This action cannot be undone.</p>
                        <div className="d-grid gap-2">
                            <Button variant="danger" className="fw-bold" onClick={handleDelete}>Delete</Button>
                            <Button variant="light" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                        </div>
                    </Modal.Body>
                </Modal>

            </div>
        </div>
    );
};

export default Staff;